import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import api, { openApi } from "../../services/api";

const useStyles = makeStyles((theme) => {
	const isDark = theme.palette.type === "dark";
	const isRTL = theme.direction === "rtl";

	const fg      = isDark ? "#e8efd8" : "#0a2620";
	const fgMuted = isDark ? "rgba(232,239,216,0.66)" : "rgba(10,38,32,0.62)";
	const accent  = isDark ? "#2ee37a" : "#128c7e";
	const hair    = isDark ? "rgba(232,239,216,0.10)" : "rgba(10,38,32,0.10)";

	return {
		root: {
			minHeight: "100dvh",
			fallbacks: [{ minHeight: "100vh" }], // older WebViews without dvh
			width: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			padding: "40px 20px",
			position: "relative",
			overflow: "hidden",
			background: isDark
				? `radial-gradient(900px 700px at 14% 16%, rgba(46,227,122,0.10), transparent 60%),
				   radial-gradient(700px 600px at 86% 84%, rgba(18,140,126,0.13), transparent 60%),
				   linear-gradient(165deg, #0a1310 0%, #060a08 100%)`
				: `radial-gradient(900px 700px at 14% 16%, rgba(37,211,102,0.14), transparent 60%),
				   radial-gradient(700px 600px at 86% 84%, rgba(18,140,126,0.08), transparent 60%),
				   linear-gradient(165deg, #eef3ec 0%, #e3ecdf 100%)`,
		},

		texture: {
			position: "absolute",
			inset: 0,
			backgroundImage: isDark
				? "radial-gradient(rgba(232,239,216,0.05) 1px, transparent 1px)"
				: "radial-gradient(rgba(10,38,32,0.07) 1px, transparent 1px)",
			backgroundSize: "28px 28px",
			pointerEvents: "none",
			zIndex: 0,
		},

		// Bouncing dots — three CSS-driven dots that scale on a staggered
		// schedule. Reads as "thinking, almost there" without the generic
		// MUI CircularProgress spinner that doesn't match the brand voice.
		dots: {
			display: "flex",
			alignItems: "center",
			gap: 10,
			marginBottom: 28,
			"& span": {
				width: 10,
				height: 10,
				borderRadius: "50%",
				background: "linear-gradient(135deg, #25d366 0%, #128c7e 100%)",
				boxShadow: `0 0 16px ${accent}`,
				animation: "$dotPulse 1.2s ease-in-out infinite",
			},
			"& span:nth-child(2)": { animationDelay: "0.18s" },
			"& span:nth-child(3)": { animationDelay: "0.36s" },
		},

		card: {
			position: "relative",
			zIndex: 1,
			maxWidth: 480,
			width: "100%",
			textAlign: "center",
			animation: "$fadeUp 0.7s cubic-bezier(0.2,0.8,0.2,1) both",
		},

		eyebrow: {
			fontSize: 11,
			fontWeight: 700,
			letterSpacing: "0.24em",
			textTransform: "uppercase",
			color: accent,
			margin: 0,
			marginBottom: 14,
		},

		title: {
			fontWeight: 500,
			fontSize: "clamp(30px, 4vw, 42px)",
			lineHeight: 1.1,
			letterSpacing: "-0.022em",
			color: fg,
			margin: 0,
			fontVariationSettings: '"opsz" 72, "SOFT" 40',
			"& em": {
				fontStyle: "italic",
				fontWeight: 400,
				color: accent,
				fontVariationSettings: '"opsz" 72, "SOFT" 100',
			},
		},

		subtitle: {
			fontSize: 15,
			fontWeight: 400,
			lineHeight: 1.55,
			color: fgMuted,
			margin: 0,
			marginTop: 12,
			maxWidth: "40ch",
			marginInline: "auto",
		},

		error: {
			marginTop: 26,
			padding: "12px 18px",
			borderRadius: 10,
			background: isDark ? "rgba(255,107,107,0.10)" : "rgba(220,38,38,0.06)",
			border: isDark ? "1px solid rgba(255,107,107,0.22)" : "1px solid rgba(220,38,38,0.18)",
			color: isDark ? "#ffb1b1" : "#b91c1c",
			fontSize: 13,
			fontWeight: 500,
			display: "inline-block",
		},

		footnote: {
			position: "absolute",
			bottom: 24,
			[isRTL ? "right" : "left"]: 0,
			right: isRTL ? 0 : "auto",
			width: "100%",
			textAlign: "center",
			fontSize: 11,
			fontWeight: 600,
			letterSpacing: "0.16em",
			textTransform: "uppercase",
			color: isDark ? "rgba(232,239,216,0.30)" : "rgba(10,38,32,0.32)",
			"& span:nth-child(2)": { opacity: 0.4, marginInline: 8 },
		},
		// Decorative hairline above footnote, brand accent
		footRule: {
			display: "inline-block",
			width: 40,
			height: 1,
			background: hair,
			verticalAlign: "middle",
			marginInlineEnd: 14,
		},

		"@keyframes dotPulse": {
			"0%, 100%": { transform: "scale(0.7)", opacity: 0.55 },
			"50%":      { transform: "scale(1)",   opacity: 1 },
		},
		"@keyframes fadeUp": {
			from: { opacity: 0, transform: "translateY(12px)" },
			to:   { opacity: 1, transform: "translateY(0)" },
		},
	};
});

/**
 * Lands users coming from whapiz.com signup. The token arrives in the URL
 * fragment (so it never hits server logs), we move it to localStorage and let
 * the existing AuthProvider take it from there.
 */
const SsoLanding = () => {
	const classes = useStyles();
	const history = useHistory();
	const [error, setError] = useState(null);

	useEffect(() => {
		const run = async () => {
			// Pull the one-shot token from the URL fragment, then strip the
			// fragment IMMEDIATELY (before any await) so it can't leak via
			// Referer, browser history, or a mid-flight navigation.
			const fragment = window.location.hash || "";
			const params = new URLSearchParams(
				fragment.startsWith("#") ? fragment.slice(1) : fragment
			);
			const token = params.get("token");
			const search = new URLSearchParams(window.location.search);

			window.history.replaceState(
				null,
				"",
				window.location.pathname + window.location.search
			);

			if (!token) {
				setError("Missing SSO token");
				setTimeout(() => history.push("/login"), 1500);
				return;
			}

			// Honor ?next= so the portal can deep-link into the CRM
			// (e.g. /connections, /tickets). Same-origin paths only — guards
			// against open-redirect.
			const rawNext = search.get("next");
			const next =
				rawNext && rawNext.startsWith("/") && !rawNext.startsWith("//")
					? rawNext
					: "/";

			try {
				// Trade the portal-issued JWT for a fresh CRM session: the
				// server verifies the signature, enforces the host<->tenant
				// match, and burns the token (single-use). openApi has no
				// auth/refresh interceptor, so a rejected token fails cleanly
				// here instead of kicking off the 401 refresh loop;
				// withCredentials lets the httpOnly refresh cookie land.
				const { data } = await openApi.post(
					"/auth/sso/exchange",
					{ token },
					{ withCredentials: true }
				);

				localStorage.setItem("token", JSON.stringify(data.token));
				if (data.user?.companyId != null)
					localStorage.setItem("companyId", String(data.user.companyId));
				if (data.user?.id != null)
					localStorage.setItem("userId", String(data.user.id));
				api.defaults.headers.Authorization = `Bearer ${data.token}`;

				// Full navigation so AuthProvider re-bootstraps from the stored
				// token — the same mechanism the password login relies on.
				window.location.replace(next);
			} catch (err) {
				const code = err?.response?.data?.error;
				setError(
					code
						? `Sign-in failed (${code}). Redirecting to login…`
						: "SSO failed. Redirecting to login…"
				);
				setTimeout(() => history.push("/login"), 1800);
			}
		};

		run();
	}, [history]);

	return (
		<div className={classes.root}>
			<div className={classes.texture} aria-hidden="true" />
			<div className={classes.card}>
				<div className={classes.dots} aria-hidden="true">
					<span />
					<span />
					<span />
				</div>

				<p className={classes.eyebrow}>Single sign-on</p>
				<h1 className={classes.title}>
					Signing you <em>in</em>…
				</h1>
				<p className={classes.subtitle}>
					Setting up your Whapiz workspace.
				</p>

				{error && <div className={classes.error}>{error}</div>}
			</div>

			<div className={classes.footnote}>
				<span className={classes.footRule} aria-hidden="true" />
				<span>Whapiz</span>
				<span>·</span>
				<span>WhatsApp CRM</span>
			</div>
		</div>
	);
};

export default SsoLanding;
